import { Check, X } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BOT_LOADING_ICONS } from '~/components/common/cycling-icon/bot-icons';
import { CyclingIcon } from '~/components/common/cycling-icon/cycling-icon';
import { Card } from '~/components/ui/card';
import { cn } from '~/utils/cn';
import { formatDurationBetween, formatRelative } from '~/utils/date';

import {
  humanisePipelineStageName,
  pipelineStageI18nKey,
  type PipelineStage,
} from '../../services/experiment-pipeline.service';

interface Props {
  className?: string;
  /**
   * Live per-stage progress from `GET /pipeline/status`. The timeline is
   * driven directly by this array — its order and contents are the source
   * of truth, so new backend stages appear automatically without a
   * frontend change. `undefined` or an empty array suppresses the timeline
   * (legacy callsites omit the prop; the page-level loading state covers
   * the "no stages stamped yet" window).
   */
  stages?: PipelineStage[] | undefined;
}

/** Canonical status values rendered by this card. The wire type is just
 *  `string`, so we keep this as `string` too and drive the status-specific
 *  visuals via equality checks against the documented values below:
 *  `'completed' | 'in_progress' | 'awaiting' | 'failed' | 'pending' | 'scheduled'`. */
type StageStatus = string;

/**
 * Full-card "Delivery in Progress" state shown in place of the Timeline
 * tab while the pipeline workflow is in flight. Optionally renders a live
 * per-stage progress timeline when `stages` is supplied.
 */
export function DeliveryInProgressCard({ className, stages }: Props) {
  const { t } = useTranslation('experiments');
  return (
    <Card
      className={cn(
        'flex min-h-[480px] flex-col items-center justify-center gap-8 p-12 text-center',
        className,
      )}
    >
      <DeliveryRing />
      <div className="space-y-1.5">
        <h2 className="text-foreground text-2xl font-bold tracking-tight">
          {t('detail.launch.title')}
        </h2>
        <p className="text-muted-foreground mx-auto max-w-md text-sm">{t('detail.launch.body')}</p>
      </div>
      {stages !== undefined && stages.length > 0 && <StageTimeline stages={stages} />}
    </Card>
  );
}

/**
 * Horizontal 4-node pipeline timeline. Each canonical stage renders as a
 * numbered circle (or a check / x for terminal states) above a label, the
 * relative time, and an optional product-count pill. The currently active
 * stage shows a floating "In Progress" badge above its circle — every
 * column reserves the badge slot at the top so the circles stay vertically
 * aligned regardless of which one is active.
 *
 * Connectors are 1px horizontal lines between adjacent nodes, colored
 * green when the LEFT node has completed (so each completed→completed and
 * completed→in-progress link reads as a finished hand-off).
 */
function StageTimeline({ stages }: { stages: PipelineStage[] }) {
  const { t } = useTranslation('experiments');
  return (
    <div className="flex w-full max-w-2xl items-start gap-0">
      {stages.map((stage, i) => {
        const status: StageStatus = stage.status;
        const isLast = i === stages.length - 1;
        return (
          <Fragment key={stage.name}>
            <StageNode index={i} name={stage.name} status={status} stage={stage} t={t} />
            {!isLast && <StageConnector completed={status === 'completed'} />}
          </Fragment>
        );
      })}
    </div>
  );
}

/** 1px connector between two adjacent stage nodes. `mt-11` aligns the line
 *  with the vertical midpoint of the 40px circle, accounting for the
 *  reserved badge slot (h-5) + its mb-1 above each circle. */
function StageConnector({ completed }: { completed: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={cn('mt-11 h-px flex-1', completed ? 'bg-success' : 'bg-border')}
    />
  );
}

type Translate = ReturnType<typeof useTranslation>['t'];

interface StageNodeProps {
  index: number;
  name: string;
  status: StageStatus;
  stage: PipelineStage;
  t: Translate;
}

function StageNode({ index, name, status, stage, t }: StageNodeProps) {
  const isCompleted = status === 'completed';
  const isInProgress = status === 'in_progress';
  const isAwaiting = status === 'awaiting';
  const isFailed = status === 'failed';
  const isActive = isInProgress || isAwaiting;

  // Column width matches the circle (w-10) so the `flex-1` connectors
  // sibling between columns run flush from one circle's right edge to the
  // next circle's left edge. Labels / time / count pill are wider than the
  // column but use `whitespace-nowrap` so they extend symmetrically beyond
  // the column via the parent's `items-center` — no horizontal clipping,
  // no wrapping. The row's `max-w-2xl` keeps adjacent label overflow from
  // colliding in normal layouts.
  return (
    <div className="flex w-10 shrink-0 flex-col items-center text-center">
      {/* Reserved badge slot — keeps every column's circle on the same
          baseline whether or not its badge is showing. Slot has no fixed
          width; the pill (wider than the column) is centered via the
          column's `items-center` and overflows symmetrically. */}
      <div className="mb-1 flex h-5 items-end">
        {isActive && (
          <span className="bg-primary/10 text-primary inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">
            {t('detail.launch.inProgressBadge')}
          </span>
        )}
      </div>

      {/* Numbered / state-icon circle */}
      <div
        className={cn(
          'flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium',
          isCompleted && 'bg-success text-white',
          isActive && 'bg-primary text-white',
          isFailed && 'bg-destructive text-white',
          status === 'pending' && 'bg-muted text-muted-foreground',
        )}
      >
        {isCompleted ? (
          <Check aria-hidden="true" className="h-5 w-5" />
        ) : isFailed ? (
          <X aria-hidden="true" className="h-5 w-5" />
        ) : (
          index + 1
        )}
      </div>

      {/* Label */}
      <p
        className={cn(
          'mt-2 text-sm font-medium whitespace-nowrap',
          isActive && 'text-primary',
          isFailed && 'text-destructive',
          status === 'pending' && 'text-muted-foreground',
        )}
      >
        {t(`detail.launch.stages.${pipelineStageI18nKey(name)}`, {
          defaultValue: humanisePipelineStageName(name),
        })}
      </p>

      {/* Timing sub-label — in-flight stages show a live "started X ago",
          terminal stages a frozen run duration. Pending nodes omit timing. */}
      {renderStageTiming(status, stage, t)}

      {/* Optional product-count pill (e.g. "45 Products" under Enrichment). */}
      {typeof stage.count === 'number' && (
        <span className="bg-muted text-muted-foreground mt-2 inline-flex rounded-full px-2 py-0.5 text-xs whitespace-nowrap">
          {t('detail.launch.stages.count', { count: stage.count })}
        </span>
      )}
    </div>
  );
}

/**
 * Timing sub-label under each node. In-flight stages (`in_progress` / `awaiting`) show a live
 * "started X ago"; terminal stages (`completed` / `failed` / `rejected`) show a FROZEN run
 * duration (startedAt→completedAt) that never ticks — a finished stage's elapsed time is a
 * measurement, not a clock. Mirrors the shared `PipelineStageTimeline.renderStageMeta` so this
 * card stays consistent with the Full Catalog Generation step; falls back to the status label
 * when a terminal stage is missing a timestamp rather than a relative time that would keep moving.
 */
function renderStageTiming(status: StageStatus, stage: PipelineStage, t: Translate): ReactNode {
  if (status === 'completed' || status === 'failed' || status === 'rejected') {
    const duration =
      stage.startedAt && stage.completedAt
        ? formatDurationBetween(stage.startedAt, stage.completedAt)
        : '';
    return (
      <p className="text-muted-foreground mt-1 text-xs whitespace-nowrap">
        {duration || t(`detail.launch.stages.${status}`)}
      </p>
    );
  }
  if ((status === 'in_progress' || status === 'awaiting') && stage.startedAt) {
    return (
      <p className="text-muted-foreground mt-1 text-xs whitespace-nowrap">
        {formatRelative(stage.startedAt)}
      </p>
    );
  }
  return null;
}

/**
 * 80px circular spinner matching the launch-state design — a muted full
 * track with a primary-colored ~270° arc rotating clockwise. `animate-spin`
 * is sufficient since the dash pattern itself draws the partial arc. The
 * spinner sits behind a centered (non-spinning) cycling icon that rotates
 * through the delivery icon set.
 */
function DeliveryRing() {
  return (
    <div className="relative h-20 w-20">
      <svg viewBox="0 0 80 80" className="h-20 w-20 animate-spin" role="img" aria-hidden="true">
        {/* Track */}
        <circle
          cx="40"
          cy="40"
          r="34"
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          className="text-muted-foreground/15"
        />
        {/* Active arc — ~75% of the circumference at r=34 (~214 px). The gap
            before the arc tail keeps the dash from joining itself. */}
        <circle
          cx="40"
          cy="40"
          r="34"
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
          className="text-primary"
          strokeDasharray="160 60"
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <CyclingIcon
          icons={BOT_LOADING_ICONS}
          className="h-7 w-7"
          iconClassName="text-primary h-7 w-7"
        />
      </div>
    </div>
  );
}
